/**
 * Browser copies of the server's form rules, so a form never sends what the server would refuse.
 *
 * Server sources (Netlify Node functions; the KGP Host PHP port in functions/_shared/ mirrors them):
 * - cleanText: netlify/functions/utils/sanitizers.js stripHtmlTags() (sanitize-html with no allowed
 *   tags, then the basic entities decoded) followed by trim(). Lengths are measured on this.
 * - isValidEmail: validator.isEmail() with the options in utils/validators.js validateEmail().
 * - isValidUsPhone: utils/validators.js validatePhone().
 *
 * src/lib/__tests__/form-rules.test.ts runs these against the real server functions.
 */

// Elements whose text sanitize-html drops along with the tag (its nonTextTags plus the
// exclusiveFilter in stripHtmlTags); an unclosed one swallows the rest of the text.
const NON_TEXT_ELEMENTS = /<(script|style|textarea|option)\b[^>]*>[\s\S]*?(?:<\/\1\s*>|$)/gi;
const COMMENTS = /<!--[\s\S]*?(?:-->|$)/g;
// A tag starts with "<" plus a letter, "!", "?" or "/letter" (htmlparser2), and an unclosed tag runs
// to the end of the text; "a < b", "<3" and a bare "</" stay text.
const TAGS = /<(?:[a-zA-Z!?]|\/[a-zA-Z])[^>]*(?:>|$)/g;

function decodeBasicEntities(text: string): string {
  return text
    .replace(/&lt;/g, '<')
    .replace(/&gt;/g, '>')
    .replace(/&quot;/g, '"')
    .replace(/&#x27;/g, "'")
    .replace(/&#39;/g, "'")
    .replace(/&#x2F;/g, '/')
    .replace(/&#x60;/g, '`')
    .replace(/&#x3D;/g, '=')
    .replace(/&amp;/g, '&');
}

/** The value as the server measures it: HTML tags removed, entities decoded, trimmed. */
export function cleanText(value: unknown): string {
  if (value === null || value === undefined) return '';
  return decodeBasicEntities(
    String(value).replace(NON_TEXT_ELEMENTS, '').replace(COMMENTS, '').replace(TAGS, ''),
  ).trim();
}

// ---------------------------------------------------------------------------------------------
// validator.isEmail (validator 13.15) with allow_display_name false, allow_utf8_local_part true,
// require_tld true, allow_ip_domain false, ignore_max_length false, allow_underscores false.
// ---------------------------------------------------------------------------------------------

/* eslint-disable no-control-regex */
const EMAIL_USER_UTF8_PART = /^[a-z\d!#$%&'*+\-/=?^_`{|}~\u00A1-\uD7FF\uF900-\uFDCF\uFDF0-\uFFEF]+$/i;
const QUOTED_EMAIL_USER_UTF8 =
  /^([\s\x01-\x08\x0b\x0c\x0e-\x1f\x7f\x21\x23-\x5b\x5d-\x7e\u00A0-\uD7FF\uF900-\uFDCF\uFDF0-\uFFEF]|(\\[\x01-\x09\x0b\x0c\x0d-\x7f\u00A0-\uD7FF\uF900-\uFDCF\uFDF0-\uFFEF]))*$/i;
/* eslint-enable no-control-regex */
const TLD = /^([a-z\u00A1-\u00A8\u00AA-\uD7FF\uF900-\uFDCF\uFDF0-\uFFEF]{2,}|xn[a-z0-9-]{2,})$/i;

/** validator's isByteLength: the UTF-8 byte length of the string. */
function byteLength(str: string): number {
  return encodeURI(str).split(/%..|./).length - 1;
}

function isFqdn(domain: string): boolean {
  const parts = domain.split('.');
  const tld = parts[parts.length - 1];
  if (parts.length < 2 || !TLD.test(tld) || /\s/.test(tld) || /^\d+$/.test(tld)) return false;
  return parts.every(
    (part) =>
      part.length <= 63 &&
      /^[a-z_\u00a1-\uffff0-9-]+$/i.test(part) &&
      !/[\uff01-\uff5e]/.test(part) &&
      !/^-|-$/.test(part) &&
      !/_/.test(part),
  );
}

export function isValidEmail(email: string): boolean {
  if (!email || email.length > 254) return false;
  const parts = email.split('@');
  const domain = parts.pop() as string;
  let user = parts.join('@');
  if (byteLength(user) > 64 || byteLength(domain) > 254) return false;
  if (!isFqdn(domain)) return false;
  if (user[0] === '"' && user[user.length - 1] === '"') {
    user = user.slice(1, user.length - 1);
    return QUOTED_EMAIL_USER_UTF8.test(user);
  }
  return user.split('.').every((part) => EMAIL_USER_UTF8_PART.test(part));
}

// ---------------------------------------------------------------------------------------------
// validatePhone: optional; otherwise a US number of at most 30 characters.
// ---------------------------------------------------------------------------------------------

const US_PHONE_PATTERNS = [
  /^\(?[2-9]\d{2}\)?[-.\s]?\d{3}[-.\s]?\d{4}$/,
  /^(\+?1[-.\s]?)?\(?[2-9]\d{2}\)?[-.\s]?\d{3}[-.\s]?\d{4}$/,
  /^[2-9]\d{9}$/,
  /^1[2-9]\d{9}$/,
];

export function isValidUsPhone(value: string): boolean {
  const trimmed = value.trim();
  if (!trimmed) return true;
  if (trimmed.length > 30) return false;
  const digitsOnly = trimmed.replace(/[\s\-.()+]/g, '');
  return US_PHONE_PATTERNS.some((pattern) => pattern.test(trimmed)) || /^1?[2-9]\d{9}$/.test(digitsOnly);
}

// ---------------------------------------------------------------------------------------------
// Result banners
// ---------------------------------------------------------------------------------------------

/**
 * Brings a result banner into view once it has rendered, so the visitor sees what happened
 * without hunting for it. Focus moves to it too (without a second jump) for screen readers.
 */
export function revealResult(ref: { readonly current: HTMLElement | null }): void {
  window.setTimeout(() => {
    const el = ref.current;
    if (!el) return;
    el.scrollIntoView({ behavior: 'smooth', block: 'center' });
    if (el.tabIndex >= 0 || el.hasAttribute('tabindex')) el.focus({ preventScroll: true });
  }, 50);
}
